import React, { useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import Layout from './components/Layout';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useFirebase } from './hooks/useFirebase';
import { sessionCleanupService } from './services/sessionCleanupService';

const AppContent: React.FC = () => {
  const { state } = useApp();
  useKeyboardShortcuts();
  useFirebase(); // Enable Firebase real-time synchronization
  
      // Initialize session cleanup service
      useEffect(() => {
        // The cleanup service starts automatically when imported
        
        // Cleanup on component unmount
        return () => {
          sessionCleanupService.stopCleanup();
        };
      }, []);

  // Handle page refresh and visibility changes - leave session properly
  useEffect(() => {
    let leaveSessionTimeout: NodeJS.Timeout | null = null;

    const leaveSession = async () => {
      if (state.currentSession && state.currentUser.id) {
        try {
          const { sessionService } = await import('./services/sessionService');
          await sessionService.leaveSession(state.currentSession.id, state.currentUser.id);
        } catch (error) {
          console.error('Failed to leave session on page refresh:', error);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden (refresh, close, tab switch)
        // Set a timeout to leave session if page doesn't become visible again
        leaveSessionTimeout = setTimeout(() => {
          leaveSession();
        }, 1000); // 1 second delay
      } else {
        // Page is visible again, cancel the leave timeout
        if (leaveSessionTimeout) {
          clearTimeout(leaveSessionTimeout);
          leaveSessionTimeout = null;
        }
      }
    };

    const handleBeforeUnload = () => {
      // Immediate leave on page unload
      leaveSession();
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup event listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (leaveSessionTimeout) {
        clearTimeout(leaveSessionTimeout);
      }
    };
  }, [state.currentSession, state.currentUser]);
  
  return <Layout />;
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
